import { useEffect, useState } from "react";

import styles from "./SupportScreen.module.css";

import SupportMain from "../components/support/SupportMain";
import SupportList from "../components/support/SupportList";
import SupportView from "../components/support/SupportView";

import Button from "../components/common/Button";
import { FAQSearchType, SearchOptions } from "../components/common/Search";
import DataProvider from "../data_provider/DataProvider";
import { useNavigate } from "react-router";
import { path } from "../components/common/Common";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export interface FAQResType {
    AUTHOR: string;
    COUNT: number;
    NAME: string;
    NO: number;
    RNUM: number;
    TITLE: string;
    WRITE_DATE: Date;
}

export type CategoryType = {
    no: number;
    name: string;
};

interface SupportProps {
    margin?: string;
    padding?: string;
    background?: string;
}

/**
 * @author khy, smyun
 * @date 2022-08-24 14:11, 2025-05-20
 * @remark 지원문의 화면
 * @param margin (optional)
 * 최상단 DOM 객체 스타일
 * @param padding (optional)
 * 최상단 DOM 객체 스타일
 * @param background (optional)
 * 최상단 DOM 객체 스타일
 * @returns Support
 */

function Support(props: SupportProps) {
    const [pageMode, setPageMode] = useState<string>("main"); // main, list, view
    const [pageType, setPageType] = useState<number>(0); // 선택한 키워드 분류(select box)
    const [postNo, setPostNo] = useState<number>(0); // 글 번호

    const [prevPageMode, setPrevPageMode] = useState<string>("main");
    const [searchOptions, setSearchOptions] = useState<SearchOptions>(); // keyword, filter

    const [categories, setCategories] = useState<CategoryType[]>([]); // FAQType response
    const [maxPage, setMaxPage] = useState(1); // 최대 페이지 수
    const [currentPage, setCurrentPage] = useState(1); // 현재 페이지 번호
    const [faqList, setFaqList] = useState<FAQResType[]>(); // FAQList response

    const [searchedPageType, setSearchedPageType] = useState<FAQSearchType>("0"); // 검색된 키워드 분류

    const [sp, setSp] = useSearchParams(); // 브라우저 뒤로가기가 동작하도록 URL 쿼리스트링을 상태의 단일 소스로 사용하여 param에 따라 뒤로가기 되도록 변경 | smyun | 250908
    const navigate = useNavigate();

    // 중복 push 방지: 같은 쿼리면 아무것도 안함
    const setParamsSafely = (params: URLSearchParams, opts?: { replace?: boolean }) => {
        const prev = sp.toString();
        const next = params.toString();
        if (prev === next) return; // 동일 상태면 히스토리 추가 금지
        setSp(params, { replace: !!opts?.replace });
    };

    // 없는 페이지 시 고객지원 메인페이지로 이동
    const goSupportHome = () => {
        // 쿼리 제거 + /inquire로 replace 이동
        setSp(new URLSearchParams(), { replace: true });
        navigate("/inquire", { replace: true });
    };

    const isValidParams = (mode: string, type: number, page: number, no: number) => {
        if (!["main", "list", "view"].includes(mode)) return false;
        if (mode === "view" && (!no || Number.isNaN(no))) return false;
        if (mode === "list") {
            if (!page || Number.isNaN(page) || page < 1) return false;
            // 카테고리 로드된 뒤에는 type 유효성도 검사 (0=전체 허용)
            if (categories.length) {
                const validTypes = new Set<number>([0, ...categories.map((c) => c.no)]);
                if (!validTypes.has(type)) return false;
            }
        }
        return true;
    };

    /* useEffect ------------------------------------------------------- */
    useEffect(() => {
        loadFAQCategories();
    }, []);

    // 키워드 검색 시(아이콘 클릭 시), 페이지 변경 시 동작
    useEffect(() => {
        if (searchOptions !== undefined) {
            reqFAQList();
            searchOptions.filter && setSearchedPageType(searchOptions?.filter.toString() as FAQSearchType);
        }
    }, [searchOptions, currentPage]);

    // URL → 상태 (뒤로가기/앞으로가기 포함해 자동 반영)
    useEffect(() => {
        const mode = (sp.get("mode") as "main" | "list" | "view") ?? "main";
        const type = Number(sp.get("type") ?? 0);
        const page = Number(sp.get("page") ?? 1);
        const no = Number(sp.get("no") ?? 0);

        if (!isValidParams(mode, type, page, no)) {
            goSupportHome();
            return;
        }

        setPageMode(mode);
        setPageType(Number.isNaN(type) ? 0 : type);
        setCurrentPage(Number.isNaN(page) ? 1 : page);
        setPostNo(Number.isNaN(no) ? 0 : no);

        // 검색어가 있으면 SearchOptions 세팅
        const keyword = sp.get("q") ?? "";
        if (mode === "list" && (keyword || type || page !== 1)) {
            setSearchOptions({ keyword, filter: type.toString() as FAQSearchType });
        }
    }, [sp]);

    // 상태 변경 → URL (히스토리 쌓임: 뒤로가기 가능)
    const pushParams = (next: { mode?: "main" | "list" | "view"; type?: number; page?: number; no?: number; q?: string; replace?: boolean }) => {
        const params = new URLSearchParams(sp);
        next.mode ? params.set("mode", next.mode) : params.delete("mode");
        if (typeof next.type === "number") params.set("type", String(next.type));
        if (typeof next.page === "number") params.set("page", String(next.page));
        if (typeof next.no === "number") params.set("no", String(next.no));
        if (typeof next.q === "string") params.set("q", next.q);
        setParamsSafely(params, { replace: next.replace });
    };

    /* function ------------------------------------------------------- */
    // API 연결 | smyun | 2025-05-20
    const loadFAQCategories = async () => {
        // 키워드 타입 요청
        const res = await DataProvider.GetFAQType();
        if (res.flag) {
            const categories = res.result;

            setCategories(categories);
        }
    };

    const reqFAQList = async () => {
        try {
            // FAQ List 페이지 수 계산
            const res = await DataProvider.GetFAQPaging({
                currentPage: String(currentPage),
                type: pageType === 0 ? "" : String(pageType),
                keyword: searchOptions?.keyword,
            });

            if (res.flag) {
                setMaxPage(res.result.maxPage);
                // FAQList 조회
                const response = await DataProvider.GetFAQList({
                    currentPage: String(currentPage),
                    type: pageType === 0 ? "" : String(pageType),
                    keyword: searchOptions?.keyword,
                });

                // 결과 전달
                if (response.flag) {
                    setFaqList(response.result);
                    // changePageMode("main", "list"); // 페이지 이동
                    // 뒤로가기 넣으면서 변경 | smyun | 250908
                    if (pageMode !== "list") {
                        changePageMode("main", "list", { replace: true }); // 내부 동기: replace
                    }
                } else if (response.desc == "FAQ 데이터 없음") {
                    setFaqList(undefined);
                    // changePageMode("main", "list");
                    // 뒤로가기 넣으면서 변경 | smyun | 250908
                    if (pageMode !== "list") changePageMode("main", "list", { replace: true });
                }
            }
        } catch (error) {
            console.error("FAQ목록 조회에 실패하였습니다.", error);
        }
    };

    // 페이지 전환(부모만 URL을 바꿈)
    // 기본은 replace=true (내부 동기·API 응답 등)
    // "사용자 액션"에서만 replace=false로 호출
    const changePageMode = (
        asis: string,
        tobe: "main" | "list" | "view",
        opts?: { replace?: boolean; q?: string; type?: number; page?: number; no?: number }
    ) => {
        setPrevPageMode(asis);
        setPageMode(tobe);

        if (tobe === "main") {
            pushParams({ mode: "main", type: 0, page: 1, no: 0, q: "", replace: opts?.replace ?? true });
        } else if (tobe === "list") {
            const type = opts?.type ?? pageType;
            const page = opts?.page ?? currentPage;
            const q = opts?.q ?? searchOptions?.keyword ?? "";
            pushParams({ mode: "list", type, page, q, replace: opts?.replace ?? true });
        } else {
            const no = opts?.no ?? postNo;
            pushParams({ mode: "view", no, replace: opts?.replace ?? true });
        }
    };

    // “이전” 버튼
    const back = () => navigate(-1);

    const element = (() => {
        switch (pageMode) {
            case "view":
                return <SupportView postNo={postNo} changePageMode={changePageMode} />;
            case "list":
                return (
                    <SupportList
                        categories={categories}
                        changePageMode={changePageMode}
                        changePageType={setPageType}
                        changePostNo={setPostNo}
                        setSearchOptions={setSearchOptions}
                        setCurrentPage={setCurrentPage}
                        currentPage={currentPage}
                        maxPage={maxPage}
                        pageType={pageType}
                        faqList={faqList}
                        searchedPageType={searchedPageType}
                        setSearchedPageType={setSearchedPageType}
                    />
                );
            default:
                return (
                    <>
                        <Helmet>
                            <title>고객지원 - 모두의지도</title>

                            <meta property="og:type" content="website" />
                            <meta name="title" property="og:title" content="고객지원 - 모두의지도" />
                            <meta name="description" property="og:description" content="궁금한 점을 빠르게 해결해드립니다" />
                            <meta name="imgage" property="og:image" content="https://modoomap.com/inquire.jpg" />
                            <meta name="url" property="og:url" content="https://modoomap.com/inquire" />
                        </Helmet>
                        <SupportMain
                            categories={categories}
                            pageType={pageType}
                            changePageMode={changePageMode}
                            changePageType={setPageType}
                            changePostNo={setPostNo}
                            setSearchOptions={setSearchOptions}
                            setSearchedPageType={setSearchedPageType}
                        />
                    </>
                );
        }
    })();

    return (
        <>
            <div className={styles.container}>
                {element}
                <div className={styles.center_align}>
                    {pageMode !== "main" ? (
                        // <Button
                        //     onClick={() => {
                        //         setPageMode(pageMode === "view" ? prevPageMode : "main");
                        //     }}
                        //     label={"이전"}
                        //     background={"#666"}
                        // />
                        <Button onClick={back} label={"이전"} background={"#666"} />
                    ) : null}
                    <Link to={path.CONTACT} style={{ textDecoration: "none" }}>
                        <Button label="기술지원 메일보내기" />
                    </Link>
                </div>
            </div>
        </>
    );
}

export default Support;
